import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiRequestError } from '../api/client';
import { api } from '../api/shop';
import type { NotificationOption, NotificationSettings as Settings } from '../api/types';

/**
 * Notification settings — GENERATED FROM THE SERVER'S CATALOGUE.
 *
 * There is no list of notifications in this file, and that is the whole design.
 * The titles, the descriptions, the grouping, which channels each one supports and
 * what it defaults to all arrive from `/notifications/settings`. Adding a
 * notification is one entry in NotificationCatalog on the server; it then appears
 * here, and on the other two consoles, with no change to any of them.
 *
 * The previous arrangement was a column per notification and a hand-written
 * checkbox per column. It worked until somebody added a notification and forgot the
 * checkbox — at which point the notification shipped with no way to switch it off,
 * and nothing failed to say so.
 *
 * ── Three decisions worth stating ────────────────────────────────────────────
 *
 *   UNSUPPORTED CHANNELS ARE NOT RENDERED, rather than rendered and disabled.
 *   There is no push template for a funding receipt; a greyed push toggle beside it
 *   is a control that does nothing, and one of those teaches people that none of
 *   them work.
 *
 *   OPERATIONAL NOTIFICATIONS ARE SHOWN, disabled, with the reason. Hiding them
 *   would be worse: somebody who cannot find "your business was suspended" in the
 *   list concludes it does not exist, and is surprised by it later.
 *
 *   SAVES ARE SPARSE — only switches that moved. Two people editing the same
 *   business's settings would otherwise overwrite each other's untouched switches
 *   with whatever their screen last loaded.
 */

type Pending = Record<string, boolean>;

const CHANNEL_LABEL: Record<string, string> = {
  email: 'Email',
  push: 'Push',
  sms: 'Text',
};

/** `key|channel`, which is exactly the unit the API stores. */
const cell = (key: string, channel: string) => `${key}|${channel}`;

export function NotificationSettings() {
  const [settings, setSettings] = useState<Settings | null>(null);

  // Held separately from `settings` so the screen reflects a switch the instant it
  // is clicked, without waiting for a round trip — and so `save` knows exactly
  // which ones moved rather than diffing two whole objects.
  const [pending, setPending] = useState<Pending>({});
  const [digestFrequency, setDigestFrequency] = useState<string | null>(null);
  const [digestHour, setDigestHour] = useState<number | null>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(() => {
    api
      .notificationSettings()
      .then((s) => {
        setSettings(s);
        setPending({});
        setDigestFrequency(null);
        setDigestHour(null);
      })
      .catch((ex: unknown) =>
        setError(
          ex instanceof ApiRequestError ? ex.message : 'Could not load your notification settings.',
        ),
      );
  }, []);

  useEffect(load, [load]);

  /** Groups in the order the catalogue returned them, not alphabetically. */
  const groups = useMemo(() => {
    if (settings === null) {
      return [];
    }

    const order: string[] = [];
    const byGroup = new Map<string, NotificationOption[]>();

    for (const option of settings.options) {
      if (!byGroup.has(option.group)) {
        byGroup.set(option.group, []);
        order.push(option.group);
      }

      byGroup.get(option.group)!.push(option);
    }

    return order.map((name) => ({ name, options: byGroup.get(name)! }));
  }, [settings]);

  function isOn(option: NotificationOption, channel: string, stored: boolean): boolean {
    // `?? stored` rather than `pending[key]` on its own: noUncheckedIndexedAccess
    // types an index read as possibly undefined, and it genuinely is for every
    // switch nobody has touched.
    return pending[cell(option.key, channel)] ?? stored;
  }

  function toggle(option: NotificationOption, channel: string, stored: boolean) {
    const key = cell(option.key, channel);
    const next = !isOn(option, channel, stored);

    setSaved(false);

    setPending((current) => {
      const copy = { ...current };

      // Moved back to where it started — so it is no longer a change, and sending
      // it would be sending a value the server already holds.
      if (next === stored) {
        delete copy[key];
      } else {
        copy[key] = next;
      }

      return copy;
    });
  }

  const dirty =
    Object.keys(pending).length > 0 || digestFrequency !== null || digestHour !== null;

  async function save() {
    if (settings === null) return;

    setBusy(true);
    setError(null);

    try {
      const updated = await api.updateNotificationSettings({
        // Split on the LAST separator, not destructured from split(): a catalogue
        // key containing a pipe would otherwise silently lose its tail, and
        // noUncheckedIndexedAccess is right that split()[0] can be undefined.
        subscriptions: Object.entries(pending).map(([composite, enabled]) => {
          const at = composite.lastIndexOf('|');

          return {
            key: composite.slice(0, at),
            channel: composite.slice(at + 1),
            enabled,
          };
        }),
        ...(digestFrequency === null ? {} : { digestFrequency }),
        ...(digestHour === null ? {} : { digestSendHourUtc: digestHour }),
      });

      setSettings(updated);
      setPending({});
      setDigestFrequency(null);
      setDigestHour(null);
      setSaved(true);
    } catch (ex) {
      setError(ex instanceof ApiRequestError ? ex.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  if (settings === null) {
    return (
      <>
        <h2>Notifications</h2>
        {error !== null && <p className="notice error" role="alert">{error}</p>}
        {error === null && <p className="muted">Loading…</p>}
      </>
    );
  }

  const frequency = digestFrequency ?? settings.digest.frequency;
  const hour = digestHour ?? settings.digest.sendHourUtc;

  return (
    <>
      {/* No page header: this is mounted inside the account page, which already
          has one. A second <h1> on the same screen is a heading structure that
          reads as two documents to a screen reader. */}
      <div className="page-head">
        <div>
          <h2>Notifications</h2>
          <p className="muted">
            What we send you, and how. Changes take effect on the next message.
          </p>
        </div>

        <button type="button" className="btn" disabled={busy || !dirty} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      {error !== null && <p className="notice error" role="alert">{error}</p>}
      {saved && <p className="notice ok" role="status">Saved.</p>}

      {settings.digest.available && (
        <section className="panel stack gap-md">
          <div className="stack gap-xs">
            <h3>How often</h3>
            <p className="muted">
              Some of these can arrive one at a time or as a single daily summary.
              Anything urgent — a suspension, an approval waiting on you — is always
              sent straight away, whatever you choose here.
            </p>
          </div>

          <div className="field">
            <label htmlFor="digest-frequency">Delivery</label>
            <select
              id="digest-frequency"
              value={frequency}
              onChange={(e) => { setDigestFrequency(e.target.value); setSaved(false); }}
            >
              <option value="immediate">Each one as it happens</option>
              <option value="daily">One summary a day</option>
            </select>
          </div>

          {frequency === 'daily' && (
            <div className="field">
              <label htmlFor="digest-hour">Send it at</label>
              <select
                id="digest-hour"
                value={hour}
                onChange={(e) => { setDigestHour(Number(e.target.value)); setSaved(false); }}
                aria-describedby="digest-hour-hint"
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>
                    {String(h).padStart(2, '0')}:00 UTC
                  </option>
                ))}
              </select>
              {/* UTC is stated rather than converted, because a summary that silently
                  moves an hour twice a year is worse than one that never moves. */}
              <span className="hint" id="digest-hour-hint">
                In UTC, so it does not shift when the clocks change.
              </span>
            </div>
          )}
        </section>
      )}

      {groups.map((group) => (
        <section key={group.name} className="stack gap-sm">
          <h3>{group.name}</h3>

          <div className="panel stack gap-md">
            {group.options.map((option) => (
              <div key={option.key} className="notif-row">
                <div className="notif-what">
                  <span className="notif-title">{option.title}</span>
                  <span className="hint">{option.description}</span>

                  {option.operational && (
                    <span className="hint">
                      Always sent — it is about your own account, and there is no
                      useful way to act on it if we do not tell you.
                    </span>
                  )}
                </div>

                <div className="notif-channels">
                  {option.channels
                    .filter((c) => c.supported)
                    .map((c) => (
                      <label
                        key={c.channel}
                        className={option.operational ? 'chip locked' : 'chip'}
                      >
                        <input
                          type="checkbox"
                          disabled={option.operational || busy}
                          checked={
                            option.operational || isOn(option, c.channel, c.enabled)
                          }
                          onChange={() => toggle(option, c.channel, c.enabled)}
                        />
                        {CHANNEL_LABEL[c.channel] ?? c.channel}
                      </label>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}
